import { Device, DeviceCommands, Devices, getApiUrl, IUpdatePhotoCommand, Targets, UpdatePhotoCommand } from "@app/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { hasMediaRequests } from "../helpers/versionCompare";

const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 90_000;

export interface DeviceSnapshotState {
    /** URL of the most recent camera snapshot (device photo, falling back to the last target photo) */
    url: string | undefined;
    /**
     * Which pipeline produced the shown image. Target photos come from the VA stream
     * (post-dewarp on Orin); device photos come from the UpdatePhotoCommand capture path,
     * which on older firmware may tap the camera before the dewarper — so the two sources
     * can differ in distortion, and calibration must use the model matching the shown image.
     */
    source: "device-photo" | "target-photo" | undefined;
    isLoading: boolean;
    /** True while an UpdatePhotoCommand is pending on the device */
    isRequesting: boolean;
    /** False when the device firmware is too old to accept photo commands */
    canRequestNewPhoto: boolean;
    /** Ask the device for a fresh camera photo (delivered on its next heartbeat) and wait for it */
    requestNewPhoto: () => Promise<boolean>;
}

/**
 * Resolves a camera snapshot for a device the same way DeviceHomography does: prefer the
 * device photo captured via UpdatePhotoCommand, else fall back to the last target photo.
 */
export function useDeviceSnapshot(device: Device | undefined): DeviceSnapshotState {
    const [url, setUrl] = useState<string | undefined>();
    const [source, setSource] = useState<"device-photo" | "target-photo" | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [isRequesting, setIsRequesting] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const resolveUrl = useCallback(async (d: Device): Promise<{ url: string; source: "device-photo" | "target-photo" } | undefined> => {
        if ((d.devicePhotoUrl?.length ?? 0) > 0) {
            return { url: getApiUrl(d.devicePhotoUrl!), source: "device-photo" as const };
        }
        const lastTarget = await Targets.getLastTargetWithPhotoForDevice(d.id);
        if (lastTarget) {
            return { url: getApiUrl(`/api/organization/${d.organizationId}/device/${d.id}/target/${lastTarget.externalId}/photo`), source: "target-photo" as const };
        }
        return undefined;
    }, []);

    useEffect(() => {
        if (!device) {
            setUrl(undefined);
            setSource(undefined);
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        resolveUrl(device)
            .then((resolved) => {
                if (!cancelled) {
                    setUrl(resolved?.url);
                    setSource(resolved?.source);
                }
            })
            .catch((err) => {
                console.error("Failed to resolve device snapshot", err);
                if (!cancelled) {
                    setUrl(undefined);
                    setSource(undefined);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [device?.id, device?.devicePhotoUrl, resolveUrl]); // eslint-disable-line react-hooks/exhaustive-deps

    const requestNewPhoto = useCallback(async (): Promise<boolean> => {
        if (!device) return false;
        setIsRequesting(true);
        try {
            const previousPhotoUrl = (await Devices.getById(device.id))?.devicePhotoUrl;
            await DeviceCommands.sendCommand(new UpdatePhotoCommand({ deviceId: device.id } as IUpdatePhotoCommand));

            // The command rides the device's next heartbeat; the upload mints a new DevicePhotoUrl.
            const deadline = Date.now() + POLL_TIMEOUT_MS;
            while (Date.now() < deadline && mountedRef.current) {
                await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
                const fresh = await Devices.getById(device.id);
                if (fresh?.devicePhotoUrl && fresh.devicePhotoUrl !== previousPhotoUrl) {
                    if (mountedRef.current) {
                        setUrl(getApiUrl(fresh.devicePhotoUrl));
                        setSource("device-photo");
                    }
                    return true;
                }
            }
            return false;
        } finally {
            if (mountedRef.current) setIsRequesting(false);
        }
    }, [device]);

    return {
        url,
        source,
        isLoading,
        isRequesting,
        canRequestNewPhoto: hasMediaRequests(device?.firmwareVersion),
        requestNewPhoto,
    };
}
